import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Palmtree, ChevronRight } from "lucide-react";

const TITLE_ORDER = ["Senior Manager", "Manager", "Supervisor", "Employee"];

function mstToday() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Edmonton" });
}

// Compact headcount banner for Senior Managers / Admins — who is on vacation
// TODAY, grouped by job position. Links through to the full On-Vacation page.
export const VacationHeadcountBanner = ({ user }) => {
  const [entries, setEntries] = useState(null);
  const canSee = user?.role === "admin" || user?.job_title === "Senior Manager";

  useEffect(() => {
    if (!canSee) return;
    const today = mstToday();
    api
      .get("/team/on-vacation", { params: { start_date: today, end_date: today } })
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => setEntries([]));
  }, [canSee]);

  const groups = useMemo(() => {
    if (!entries) return [];
    const seen = new Set();
    const byTitle = {};
    for (const e of entries) {
      if (e.type !== "vacation" || !e.is_active_today) continue;
      const u = e.user || {};
      if (seen.has(u.id)) continue;
      seen.add(u.id);
      const title = u.job_title || "Employee";
      (byTitle[title] = byTitle[title] || []).push(u.display_name);
    }
    return Object.entries(byTitle)
      .sort((a, b) => {
        const ia = TITLE_ORDER.indexOf(a[0]);
        const ib = TITLE_ORDER.indexOf(b[0]);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
      .map(([title, names]) => ({ title, names: names.sort() }));
  }, [entries]);

  if (!canSee || entries === null) return null;
  const total = groups.reduce((n, g) => n + g.names.length, 0);

  return (
    <Link
      to="/team/on-vacation"
      className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-2.5 hover:bg-amber-50 transition-colors"
      data-testid="vacation-headcount-banner"
    >
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-amber-800">
        <Palmtree className="w-3.5 h-3.5" />
        On vacation today
        <span className="px-1.5 py-0.5 rounded-full bg-amber-200/80 text-amber-900 font-semibold" data-testid="vacation-headcount-total">{total}</span>
      </span>
      {total === 0 ? (
        <span className="text-xs text-amber-700/80">Full headcount — nobody on vacation.</span>
      ) : (
        groups.map((g) => (
          <span key={g.title} className="text-xs text-amber-900" data-testid={`vacation-group-${g.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className="font-semibold">{g.title}s ({g.names.length}):</span>{" "}
            {g.names.join(", ")}
          </span>
        ))
      )}
      <ChevronRight className="w-3.5 h-3.5 text-amber-500 ml-auto" />
    </Link>
  );
};

export default VacationHeadcountBanner;
